import { DatePipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ServicecallService } from 'src/app/services';
import { VgApiService } from '@videogular/ngx-videogular/core';
import { Subscription } from 'rxjs';
import { TrackerServiceService } from 'src/app/modules/tracker/tracker-service.service';
import { Messages } from 'src/app/constants/messages';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

interface VideoClip {
  id: string;
  camera: string;
  file: string;
  url?: SafeUrl | null;
  blobUrl?: string; // raw blob URL for revocation
}

@Component({
  selector: 'app-events-video-evidence-popover',
  templateUrl: './events-video-evidence-popover.component.html',
  styleUrl: './events-video-evidence-popover.component.css'
})
export class EventsVideoEvidencePopoverComponent implements OnInit, OnDestroy {

  tenantConfig: any;
  alertDetails: any[] = [];
  api: VgApiService;
  isAiFeed = false;
  videoClips: VideoClip[] = [];
  activeClip: VideoClip | null = null;
  streamBaseUrl: string = '';
  streamAnomalyCodes: any;

  private _fetchClipSubscription: Subscription;
  private _streamSubscription: Subscription;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _dialogRef: MatDialogRef<any>,
    private _service: ServicecallService,
    private _datePipe: DatePipe,
    private _trackerService: TrackerServiceService,
    private _sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    this.tenantConfig = this._service?.getConfiguration('tenantGenericConfig') ?? {};
    this.streamAnomalyCodes = this._service?.getConfiguration('streamAnomalyCodes') ?? {};
    this.alertDetails = this.tenantConfig?.eventsEvidenceSummaryDetailsList ?? [];
    this._mapData();
    this.fetchEvidenceClipsFileNames();
  }

  ngOnDestroy() {
    this._fetchClipSubscription?.unsubscribe();
    this._streamSubscription?.unsubscribe();

    this.videoClips.forEach(clip => {
      if (clip.blobUrl) URL.revokeObjectURL(clip.blobUrl);
    });
  }

  closeDialog() {
    this._dialogRef.close(true);
  }

  private _mapData() {
    const event = this.data?.eventDetails;
    this.alertDetails = this.alertDetails.map(item => {
      const dataKey = item.key;
      let rawValue = event[dataKey];
      if (item?.key === 'originalTime' && rawValue) {
        rawValue = this._datePipe?.transform(rawValue, 'd MMM yyyy, hh:mm:ss a');
      }
      return {
        ...item,
        value: rawValue !== null ? rawValue : '--'
      };
    });
  }

  setClip(clip: VideoClip) {
    this.activeClip = clip;

    if (clip.url) return;

    this.streamVideo();
  }

  onPlayerReady(api: VgApiService) {
    this.api = api;
  }

  toggleAiFeed() {
    this.isAiFeed = !this.isAiFeed;
  }

  fetchEvidenceClipsFileNames() {
    const reqPayload = {
      anomalyCode: this.streamAnomalyCodes?.[this.data?.eventDetails?.alertId?.toString()],
      timestamp: this._datePipe.transform(this.data?.eventDetails?.sourceTimestamp ?? this.data?.eventDetails?.originalTime, 'yyyy-MM-ddTHH:mm:ssZZ'),
      line: this.data?.eventDetails?.zoneName
    };

    this._fetchClipSubscription = this._trackerService?.fetchEvidenceClips(reqPayload)?.subscribe({
      next: (result: any) => {
        this.streamBaseUrl = result?.streamBaseUrl ?? '';

        this.videoClips = (result?.clips ?? [])?.map((clip: any, index: number) => ({
          ...clip,
          id: `${clip.camera}-${index}`,
          url: null
        }));

        if (this.videoClips.length) {
          this.setClip(this.videoClips[0]);
        }
      },
      error: () => {
        this._service.toasterService({ type: 'error', msg: Messages.ERROR });
      }
    });
  }

  streamVideo() {
    if (!this.activeClip) return;

    const streamUrl = this.streamBaseUrl + this.activeClip.file;

    this._streamSubscription?.unsubscribe();

    this._streamSubscription = this._trackerService.videoStream(streamUrl)?.subscribe({
      next: (response: Blob) => {
        if (this.activeClip?.blobUrl) {
          URL.revokeObjectURL(this.activeClip.blobUrl);
        }

        const blobUrl = URL.createObjectURL(response);
        const safeUrl = this._sanitizer.bypassSecurityTrustUrl(blobUrl);

        this.videoClips = this.videoClips?.map(c =>
          c.id === this.activeClip?.id
            ? { ...c, url: safeUrl, blobUrl }
            : c
        );

        this.activeClip = this.videoClips?.find(c => c.id === this.activeClip?.id) ?? this.activeClip;
      },
      error: () => {
        this._service.toasterService({ type: 'error', msg: Messages.ERROR });
      }
    });
  }

  formatToSQLDateTime(date: Date | string): string {
    if (!date) return '';

    const d = date instanceof Date ? date : new Date(
      date.replace(/([+-]\d{2})(\d{2})$/, '$1:$2')
    );

    if (isNaN(d.getTime())) return '';

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }
}
